/* global paypal, braintree */
import logger from 'components/utils/logger'
import { trackCheckoutError } from '../../../utils/checkoutErrors'

// Initialise the Braintree-PayPal handshake and render the PayPal Smart Button(s).
//
// Behaviour by mode:
//   * Default (recurring vault) — single PayPal button rendered into
//     `#paypal-button-container`. flow:'vault', intent:'authorize'. Used by /checkout
//     subscribe and /review_order cart flows.
//   * options.enablePayLater === true — Lifetime checkout. Renders TWO buttons:
//     PayPal into `#paypal-paypal-button-container` and Pay Later into
//     `#paypal-paylater-button-container`. flow:'checkout', intent:'capture'.
//
// NOTE: with the standalone paypal.Buttons() API a *forced* fundingSource is
// NOT silently hidden when ineligible — .render() throws "<source> is not
// eligible" (region/amount dependent, common in sandbox). Each button is
// therefore gated on .isEligible() so an ineligible Pay Later just no-ops.
//
// The callback signature is unchanged from the previous v4 implementation:
//   isSubscribing → callback(nonce, order, plan, usedFreeTrial, userEmail)
//   else          → callback(nonce, order, deviceData)
//
// options.isActive (optional): whether the caller still wants these buttons.
// The handshake is async, so the page can be gone by the time it finishes
// (e.g. a successful subscribe navigated to /subscription/confirmation):
// rendering then throws "Document is ready and element … does not exist",
// and errors the SDK reports for a button nobody shows are not checkout
// failures.
export default (
  authorization,
  amount,
  callback,
  order,
  isSubscribing,
  plan,
  usedFreeTrial,
  deviceData,
  userEmail,
  options = {}
) =>
  braintree.client.create({ authorization }, (clientErr, clientInstance) => {
    if (clientErr) {
      logger.error(`Error creating client: ${clientErr}`)
      return
    }

    braintree.paypalCheckout.create(
      { client: clientInstance },
      (paypalCheckoutErr, paypalCheckoutInstance) => {
        if (paypalCheckoutErr) {
          logger.error(`Error creating PayPal Checkout: ${paypalCheckoutErr}`)
          return
        }

        const isLifetime = Boolean(options.enablePayLater)
        const isActive =
          typeof options.isActive === 'function' ? options.isActive : () => true

        const fundingSources = isLifetime
          ? [paypal.FUNDING.PAYPAL, paypal.FUNDING.PAYLATER]
          : [paypal.FUNDING.PAYPAL]

        // 'vault' only for true recurring subscriptions (need a PayPal
        // Billing Agreement for the next billing cycle). One-time cart
        // purchases and Lifetime use 'checkout' so the order's intent
        // matches the PayPal SDK URL's intent=authorize.
        const useVaultFlow = isSubscribing && !isLifetime

        const onApprove = (data) =>
          paypalCheckoutInstance.tokenizePayment(data).then((payload) => {
            if (isSubscribing)
              return callback(
                payload.nonce,
                order,
                plan,
                usedFreeTrial,
                userEmail
              )
            return callback(payload.nonce, order, deviceData)
          })

        const buildOnError = (fundingSource) => (err) => {
          // Braintree wraps a gateway 422 as a generic "PayPal payment
          // options are invalid." — the actionable reason is nested in
          // err.details.originalError, so surface it explicitly.
          const original = err && err.details && err.details.originalError
          logger.error(
            `PayPal button error (${String(fundingSource)}): ${err}${
              original ? ` | gateway: ${JSON.stringify(original)}` : ''
            }`
          )
          // A button whose page is gone (or that a newer init replaced)
          // is not a checkout the customer is looking at
          if (!isActive()) return
          // SPEC-077: PayPal shows its own error in the popup; record that
          // it happened (the braintree code only, never the gateway body)
          trackCheckoutError({
            stage: 'paypal',
            reason: 'paypal_error',
            braintreeCode: err && err.code
          })
        }

        // SPEC-077 FR-4: closing the PayPal window is not an error, but it is
        // friction worth counting
        const onCancel = () => {
          if (!isActive()) return
          trackCheckoutError({ stage: 'paypal', reason: 'paypal_cancelled' })
        }

        fundingSources.forEach((fundingSource) => {
          const containerSelector = isLifetime
            ? `#paypal-${
                fundingSource === paypal.FUNDING.PAYLATER ? 'paylater' : 'paypal'
              }-button-container`
            : '#paypal-button-container'

          // PayPal SDK v6 splits the create-payment callback by flow:
          //   * one-time (checkout): createOrder
          //   * vault (billing agreement): createBillingAgreement
          // Passing createOrder to a vault-loaded SDK throws synchronously
          // at button construction: "Must pass createBillingAgreement with
          // intent=tokenize". Vault createPayment also doesn't accept
          // amount/currency/intent (those are checkout-flow only).
          const buttonConfig = useVaultFlow
            ? {
                fundingSource,
                style: { shape: 'rect', layout: 'vertical' },
                createBillingAgreement: () =>
                  paypalCheckoutInstance.createPayment({ flow: 'vault' }),
                onApprove,
                onCancel,
                onError: buildOnError(fundingSource)
              }
            : {
                fundingSource,
                style: { shape: 'rect', layout: 'vertical' },
                createOrder: () =>
                  paypalCheckoutInstance.createPayment({
                    flow: 'checkout',
                    amount,
                    currency: 'USD',
                    intent: 'authorize'
                  }),
                onApprove,
                onCancel,
                onError: buildOnError(fundingSource)
              }

          const button = paypal.Buttons(buttonConfig)

          if (!button.isEligible()) {
            if (fundingSource === paypal.FUNDING.PAYLATER) return
            logger.error(
              `PayPal funding source not eligible: ${String(fundingSource)}`
            )
            return
          }

          // The caller unmounted or re-initialised while the handshake ran,
          // or the container is no longer in the page
          if (!isActive() || !document.querySelector(containerSelector)) return
          button.render(containerSelector)
        })
      }
    )
  })
